import qrcode from 'qrcode-generator';

const DEFAULT_WIDTH_MM = 100;
const DEFAULT_HEIGHT_MM = 70;

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const positiveNumberOrDefault = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const truncateText = (value, maxLength) => Array.from(String(value ?? '').trim())
  .slice(0, maxLength)
  .join('');

const formatDecimal = (value, decimalPlaces = 2) => {
  if (value === '' || value === null || value === undefined) return '-';
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed.toFixed(decimalPlaces) : String(value);
};

export const buildWipQrValue = (data) => [
  ...(data.id ? [`ID=${data.id}`] : []),
  `MAT=${data.matCode || ''}`,
  `LOT=${data.lot || ''}`,
  `BATCH=${data.batch || ''}`,
  `SHIFT=${data.shift || ''}`,
  `START=${data.start || ''}`,
  `END=${data.end || ''}`,
  `WEIGHT=${data.weight ?? ''}`,
  `QTY=${data.qty ?? ''}`,
  `NO=${data.no || ''}`,
].join('|');

export const createWipQrDataUrl = (data) => {
  const qr = qrcode(0, 'M');
  qr.addData(buildWipQrValue(data));
  qr.make();
  return qr.createDataURL(5, 20);
};

export const buildWipStickerFileName = (data) => {
  const safe = (value, fallback) => String(value || fallback)
    .trim()
    .replace(/[^a-zA-Z0-9ก-๙_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return [
    'WIP',
    safe(data.matCode, 'NO-MAT'),
    safe(data.lot, 'NO-LOT'),
    safe(data.batch, 'NO-BATCH'),
  ].join('_');
};

export const buildWipStickerHtml = (data, options = {}) => {
  const widthMm = positiveNumberOrDefault(
    options.widthMm ?? process.env.REACT_APP_WIP_STICKER_WIDTH_MM,
    DEFAULT_WIDTH_MM,
  );
  const heightMm = positiveNumberOrDefault(
    options.heightMm ?? process.env.REACT_APP_WIP_STICKER_HEIGHT_MM,
    DEFAULT_HEIGHT_MM,
  );
  const display = (value) => escapeHtml(value || '-');
  const quantity = escapeHtml(formatDecimal(data.qty));
  const weight = escapeHtml(formatDecimal(data.weight));
  const component = escapeHtml(truncateText(data.component ?? data.components, 30) || '-');
  const qrDataUrl = options.qrDataUrl || createWipQrDataUrl(data);
  const documentTitle = escapeHtml(options.fileName || buildWipStickerFileName(data));

  return `<!doctype html>
<html lang="th">
  <head>
    <meta charset="utf-8" />
    <title>${documentTitle}</title>
    <style>
      @page { size: ${widthMm}mm ${heightMm}mm; margin: 0; }
      * { box-sizing: border-box; }
      html, body { margin: 0 !important; padding: 0 !important; width: ${widthMm}mm; height: ${heightMm}mm; overflow: hidden; }
      body { font-family: Arial, "Noto Sans Thai", Tahoma, sans-serif; color: #000; }
      .sticker { width: 100%; height: 100%; border: 0; padding: 4mm; }
      .top { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr); gap: 2mm; font-size: 12pt; font-weight: 700; line-height: 1.2; }
      .top > div, .summary > div, .times > div { min-width: 0; overflow-wrap: anywhere; }
      .product { margin-top: 1mm; font-size: 12pt; font-weight: 700; white-space: normal; overflow-wrap: anywhere; line-height: 1.2; }
      .summary { display: grid; grid-template-columns: 30mm minmax(0, 1fr); gap: 1mm; margin-top: 1mm; font-size: 10pt; line-height: 1.2; }
      .component { min-width: 0; white-space: nowrap; overflow: hidden; }
      .process { margin-top: .6mm; font-size: 10pt; line-height: 1.2; white-space: nowrap; overflow: hidden; }
      .times { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 1mm; margin-top: .8mm; font-size: 9.5pt; }
      .lower { display: grid; grid-template-columns: minmax(0, 1fr) 32mm; gap: 2mm; margin-top: 1mm; }
      .details { display: flex; min-width: 0; flex-direction: column; justify-content: space-between; font-size: 10pt; line-height: 1.2; }
      .row { display: flex; flex-wrap: wrap; align-items: baseline; column-gap: 1mm; white-space: normal; overflow-wrap: anywhere; }
      .row > span { min-width: 0; max-width: 100%; }
      .row.wrap { white-space: normal; }
      .field-row { display: grid; grid-template-columns: 30mm max-content minmax(0, 1fr); gap: .7mm; align-items: baseline; white-space: nowrap; }
      .field-row > span { min-width: 0; overflow: hidden; }
      .first-field { white-space: nowrap; }
      .ref-text { overflow: hidden; }
      .strong { font-weight: 700; }
      .qr { width: 32mm; height: 32mm; object-fit: contain; align-self: end; image-rendering: pixelated; }
    </style>
  </head>
  <body>
    <section class="sticker">
      <div class="top">
        <div>วันที่ ${display(data.stickerDate)} &nbsp; ${display(data.shift)}</div>
        <div>รหัสสินค้า : ${display(data.matCode)}</div>
      </div>
      <div class="product">ชื่อสินค้า : ${display(data.productName)}</div>
      <div class="summary">
        <div>ประเภท : ${display(data.type)}</div>
        <div class="component">วัตถุดิบ : ${component}</div>
      </div>
      <div class="process">ขั้นตอน : ${display(data.processStep)}</div>
      <div class="times">
        <div>เริ่ม : ${display(data.start)}</div>
        <div>เสร็จ : ${display(data.end)}</div>
        <div>เวลา : ${display(data.delayTime)}</div>
      </div>
      <div class="lower">
        <div class="details">
          <div class="row"><span>วันที่ผลิต :</span><span>${display(data.productionDate)}</span></div>
          <div class="row wrap"><span>ควรใช้ภายใน :</span><span>${display(data.useWithin)}</span></div>
          <div class="field-row">
            <span class="first-field">Lot No : ${display(data.lot)}</span>
            <span>Batch :</span>
            <span>${display(data.batch)}</span>
          </div>
          <div class="field-row">
            <span class="first-field strong">นน : ${weight} KG</span>
            <span>Ref :</span>
            <span class="ref-text">${display(data.ref)}</span>
          </div>
          <div class="field-row">
            <span class="first-field strong">จำนวน : ${quantity} ${display(data.qtyUnit || data.unit)}</span>
            <span>No :</span>
            <span>${display(data.no)}</span>
          </div>
          <div class="row wrap"><span>พนักงาน :</span><span>${display(data.operator)}</span></div>
        </div>
        <img class="qr" src="${qrDataUrl}" alt="WIP QR Code" />
      </div>
    </section>
  </body>
</html>`;
};

export const printWipSticker = (data, options = {}) => new Promise((resolve, reject) => {
  if (typeof document === 'undefined') {
    reject(new Error('Printing is only available in a browser.'));
    return;
  }

  const iframe = document.createElement('iframe');
  iframe.setAttribute('title', 'WIP sticker print frame');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.visibility = 'hidden';

  const cleanup = () => window.setTimeout(() => iframe.remove(), 500);

  iframe.onload = () => {
    try {
      const printWindow = iframe.contentWindow;
      if (!printWindow) throw new Error('Unable to open the sticker print frame.');
      printWindow.onafterprint = cleanup;
      printWindow.focus();
      printWindow.print();
      window.setTimeout(cleanup, 30000);
      resolve();
    } catch (error) {
      iframe.remove();
      reject(error);
    }
  };

  iframe.srcdoc = buildWipStickerHtml(data, options);
  document.body.appendChild(iframe);
});

